import { useContext, useEffect, useState } from 'react';
import { CategoryContext } from '../../providers/CategoryContext';
import { AuthContext } from '../../providers/AuthContext';
import { showConfirm, showError, showSuccess } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import Loader from '../shared/Loader';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const StockManager = () => {
  const { categories } = useContext(CategoryContext);
  const { token } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [savingProductId, setSavingProductId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bulkStock, setBulkStock] = useState('');
  const [stockDrafts, setStockDrafts] = useState({});

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '1000', includeInactive: 'true' });
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products?${params.toString()}`);
      if (!response.ok) throw new Error('No se pudieron cargar los productos');
      const data = await response.json();
      setProducts(Array.isArray(data) ? data : (data.products || []));
    } catch (error) {
      console.error('Error al cargar stock:', error);
      showError('No se pudo cargar el inventario de productos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const getCategoryName = (product) => product.category?.name || product.category || 'Sin categoría';

  const getProductStock = (product) => {
    const numericStock = Number(product?.stock);
    return Number.isFinite(numericStock) ? numericStock : null;
  };

  const getStockDraftValue = (product) => {
    const draftValue = stockDrafts[product._id];
    if (draftValue !== undefined) return draftValue;
    return product.stock ?? '';
  };

  const filteredProducts = products.filter((product) => {
    const categoryName = getCategoryName(product);
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const matchesCategory = !selectedCategory || product.category?._id === selectedCategory;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && product.isActive !== false) ||
      (statusFilter === 'inactive' && product.isActive === false);

    if (!normalizedSearch) return matchesCategory && matchesStatus;

    const searchableText = [product.name, categoryName, product.subcategory, product.description]
      .filter(Boolean).join(' ').toLowerCase();
    return matchesCategory && matchesStatus && searchableText.includes(normalizedSearch);
  });

  const totalUnits = filteredProducts.reduce((total, product) => total + (getProductStock(product) || 0), 0);
  const outOfStockCount = filteredProducts.filter((product) => getProductStock(product) === 0).length;
  const undefinedStockCount = filteredProducts.filter((product) => getProductStock(product) === null).length;
  const inStockCount = filteredProducts.filter((product) => getProductStock(product) > 0).length;

  const handleStockDraftChange = (productId, value) => {
    setStockDrafts((prev) => ({ ...prev, [productId]: value }));
  };

  const updateLocalProductStock = (productId, stock) => {
    setProducts((prev) => prev.map((p) => p._id === productId ? { ...p, stock } : p));
  };

  const clearStockDraft = (productId) => {
    setStockDrafts((prev) => { const next = { ...prev }; delete next[productId]; return next; });
  };

  const validateStock = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) return null;
    return parsed;
  };

  const handleSaveProductStock = async (product) => {
    const nextStock = validateStock(getStockDraftValue(product));
    if (nextStock === null) { showError('El stock debe ser un número entero mayor o igual a 0'); return; }
    setSavingProductId(product._id);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${product._id}`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ stock: nextStock })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo actualizar el stock del producto');
      updateLocalProductStock(product._id, data.stock ?? nextStock);
      clearStockDraft(product._id);
      showSuccess(`Stock actualizado para "${product.name}"`);
    } catch (error) {
      console.error('Error al guardar stock individual:', error);
      showError(error.message || 'Hubo un problema al guardar el stock');
    } finally {
      setSavingProductId(null);
    }
  };

  const handleBulkStockUpdate = async () => {
    const nextStock = validateStock(bulkStock);
    if (nextStock === null) { showError('Ingresa un stock entero mayor o igual a 0'); return; }
    if (filteredProducts.length === 0) { showError('No hay productos para actualizar con los filtros actuales'); return; }

    const selectedCategoryName = categories.find((c) => c._id === selectedCategory)?.name;
    const scopeLabel = selectedCategoryName ? `la categoría "${selectedCategoryName}"` : 'los productos filtrados';

    const result = await showConfirm(
      `Se actualizarán ${filteredProducts.length} productos con stock ${nextStock}.`,
      `¿Aplicar stock masivo a ${scopeLabel}?`
    );
    if (!result.isConfirmed) return;

    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/bulk-stock`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ stock: nextStock, productIds: filteredProducts.map((p) => p._id) })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo aplicar el stock masivo');

      setProducts((prev) =>
        prev.map((p) => filteredProducts.some((fp) => fp._id === p._id) ? { ...p, stock: nextStock } : p)
      );
      setStockDrafts({});
      showSuccess(`${data.modifiedCount ?? filteredProducts.length} productos actualizados`);
    } catch (error) {
      console.error('Error al aplicar stock masivo:', error);
      showError(error.message || 'Hubo un problema al actualizar el stock');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="stockmgr-panel">
      {/* Header */}
      <div className="stockmgr-header">
        <div>
          <h1>Gestor de Stock</h1>
          <p>Edita el stock de toda la tienda, filtrando por categoría o búsqueda</p>
        </div>
        <button className="stockmgr-reload-btn" onClick={fetchProducts}>
          <i className="fas fa-arrows-rotate"></i> Recargar
        </button>
      </div>

      {/* KPI Row */}
      <div className="stockmgr-kpi-row">
        <div className="stockmgr-kpi">
          <div className="stockmgr-kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
            <i className="fas fa-boxes-stacked"></i>
          </div>
          <div className="stockmgr-kpi-info">
            <span className="stockmgr-kpi-value">{filteredProducts.length}</span>
            <span className="stockmgr-kpi-label">Productos Filtrados</span>
          </div>
        </div>
        <div className="stockmgr-kpi">
          <div className="stockmgr-kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
            <i className="fas fa-cubes"></i>
          </div>
          <div className="stockmgr-kpi-info">
            <span className="stockmgr-kpi-value">{totalUnits.toLocaleString()}</span>
            <span className="stockmgr-kpi-label">Unidades Totales</span>
          </div>
        </div>
        <div className="stockmgr-kpi">
          <div className="stockmgr-kpi-icon" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
            <i className="fas fa-triangle-exclamation"></i>
          </div>
          <div className="stockmgr-kpi-info">
            <span className="stockmgr-kpi-value">{outOfStockCount}</span>
            <span className="stockmgr-kpi-label">Agotados</span>
          </div>
        </div>
        <div className="stockmgr-kpi">
          <div className="stockmgr-kpi-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
            <i className="fas fa-question-circle"></i>
          </div>
          <div className="stockmgr-kpi-info">
            <span className="stockmgr-kpi-value">{undefinedStockCount}</span>
            <span className="stockmgr-kpi-label">Sin Definir</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="stockmgr-toolbar">
        <div className="stockmgr-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar producto por nombre, categoría..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="stockmgr-search-clear" onClick={() => setSearchQuery('')}>
              <i className="fas fa-xmark"></i>
            </button>
          )}
        </div>
        <select
          className="stockmgr-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {categories.map((cat) => (
            <option key={cat._id} value={cat._id}>{cat.name}</option>
          ))}
        </select>
        <select
          className="stockmgr-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos</option>
          <option value="active">Solo activos</option>
          <option value="inactive">Solo inactivos</option>
        </select>
      </div>

      {/* Bulk Update Card */}
      <div className="stockmgr-bulk-card">
        <div className="stockmgr-bulk-header">
          <i className="fas fa-layer-group"></i>
          <h3>Actualización Masiva</h3>
        </div>
        <div className="stockmgr-bulk-body">
          <div className="stockmgr-bulk-input-group">
            <label>Nuevo stock</label>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="Ej: 15"
              value={bulkStock}
              onChange={(e) => setBulkStock(e.target.value)}
            />
          </div>
          <div className="stockmgr-bulk-info">
            Se aplicará a <strong>{filteredProducts.length}</strong> producto{filteredProducts.length !== 1 ? 's' : ''} filtrados.
          </div>
          <button
            className="stockmgr-bulk-btn"
            onClick={handleBulkStockUpdate}
            disabled={updating || filteredProducts.length === 0}
          >
            {updating ? (
              <><i className="fas fa-spinner fa-spin"></i> Aplicando...</>
            ) : (
              <><i className="fas fa-check"></i> Aplicar Stock Masivo</>
            )}
          </button>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="stockmgr-table-card">
        <div className="stockmgr-table-header">
          <h3><i className="fas fa-list"></i> Inventario</h3>
          <span className="stockmgr-table-count">
            {inStockCount} con stock · {outOfStockCount} agotados · {undefinedStockCount} sin definir
          </span>
        </div>
        <div className="stockmgr-table-wrapper">
          <table className="stockmgr-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Estado</th>
                <th>Stock Actual</th>
                <th style={{ minWidth: '200px' }}>Editar Stock</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="stockmgr-empty-row">
                    <i className="fas fa-inbox"></i>
                    No hay productos para mostrar con los filtros actuales.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const stockValue = getProductStock(product);
                  const image = product.images?.[0] || product.image;

                  return (
                    <tr key={product._id} className={product.isActive === false ? 'stockmgr-row-inactive' : ''}>
                      <td>
                        <div className="stockmgr-product-cell">
                          {image ? (
                            <img src={getDisplayImageUrl(image)} alt={product.name} className="stockmgr-thumb" />
                          ) : (
                            <div className="stockmgr-thumb-placeholder">
                              <i className="fas fa-box"></i>
                            </div>
                          )}
                          <div>
                            <div className="stockmgr-product-name">{product.name}</div>
                            <small className="stockmgr-product-sub">{product.subcategory || ''}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="stockmgr-cat-badge">{getCategoryName(product)}</span>
                      </td>
                      <td>
                        <span className={`stockmgr-status ${product.isActive === false ? 'inactive' : 'active'}`}>
                          <span className="stockmgr-status-dot"></span>
                          {product.isActive === false ? 'Inactivo' : 'Activo'}
                        </span>
                      </td>
                      <td>
                        {stockValue === null ? (
                          <span className="stockmgr-stock-pill undef">Sin definir</span>
                        ) : stockValue === 0 ? (
                          <span className="stockmgr-stock-pill out">Agotado</span>
                        ) : (
                          <span className="stockmgr-stock-pill ok">{stockValue} uds.</span>
                        )}
                      </td>
                      <td>
                        <div className="stockmgr-edit-row">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className="stockmgr-edit-input"
                            value={getStockDraftValue(product)}
                            onChange={(e) => handleStockDraftChange(product._id, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); handleSaveProductStock(product); }
                            }}
                          />
                          <button
                            className="stockmgr-save-btn"
                            onClick={() => handleSaveProductStock(product)}
                            disabled={savingProductId === product._id}
                          >
                            {savingProductId === product._id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-check"></i>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockManager;
